import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Search, XCircle } from "react-bootstrap-icons";
import Dimensions from "react-dimensions";
import { BowlerCard } from "../ui/BowlerCard";
import { HeaderActionButton } from "./BowlerHeader";
import styled, { css, keyframes } from "styled-components";
import { getPhotoForUserId } from "../../utils/utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTranslation } from "react-i18next";
import { DEBOUNCE_INPUT_PROPS } from "../../const";
import { DebounceInput } from "react-debounce-input";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import { Select } from "antd";

// How many rows before the end of the loaded data we start prefetching.
const PREFETCH_ROWS = 5;

const Label = styled("label")`
  position: absolute;
  right: 0px;
  left: auto;
  color: ${(props) => (!props.ShowPlaceholder ? "#000000" : "#00000000")};

  @media (max-width: 576px) and (orientation: Portrait) {
    color: ${(props) => (!props.ShowPlaceholder ? "#000000" : "#00000000")};
  }
`;

const Button = styled.button`
  background: #00187e 0% 0% no-repeat padding-box;
  border-radius: 8px;
  opacity: 1;
  width: 100%;
  height: 48px;
  margin-top: 1rem;
  font-weight: 700;

  ${(props) =>
    props.subs &&
    css`
      background: #00becc 0% 0% no-repeat padding-box;
      margin-bottom: 3vh;
    `}
`;

const ButtonSearch = styled.button`
  margin: 0;
  width: 40px;
  height: 40px;
  background: #00187e;
  color: #ffffff;
  border: none;
  opacity: 1;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.18s ease, transform 0.12s ease;

  &:hover {
    background: #001463;
  }
  &:active {
    transform: scale(0.94);
  }
`;

const ButtonReset = styled.button`
  margin: 0;
  width: 40px;
  height: 40px;
  background: #eef0f5;
  color: #5a607a;
  border: none;
  opacity: 1;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.18s ease, transform 0.12s ease;

  &:hover {
    background: #e2e4ee;
  }
  &:active {
    transform: scale(0.94);
  }
`;

const SearchGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  gap: 12px;

  label {
    transform: scale(0.85) translateY(-0.3rem) translateX(0.15rem) !important;
  }

  input {
    padding-top: 0.625rem !important;
    padding-bottom: 0.625rem !important;
  }
  @media only screen and (max-width: 450px) and (orientation: portrait) {
    flex-direction: column;
    margin-bottom: 10px;
  }
`;
const selectStyle = {
  width: 150,
};
const FloatingLabelOrderBy = styled(FloatingLabel)`
  & label {
    color: #00297a !important;
    transform: scale(0.65) translateY(-1rem) translateX(0.15rem) !important;
  }

  font: normal normal bold 20px / 28px Lato;
  letter-spacing: 0px;
  opacity: 1;
  font-weight: 700;
  order: 1;
`;
const SelectArsenal = styled(Select)`
  width: 100%;
  height: 48px;

  & .ant-select-selector {
    height: 48px !important;
    border-radius: 8px !important;
    border-color: #e2e4ee !important;
  }

  &.arsenal-select .ant-select-selector {
    overflow: auto;
  }

  &.arsenal-select .ant-select-selector .ant-select-selection-item {
    line-height: 48px !important;
  }

  &.arsenal-select
    .ant-select-selector
    .ant-select-selection-overflow-item
    .ant-select-selection-item {
    line-height: 24px !important;
  }
`;
const ContainerHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: ${(props) => `0 ${props.marginFill}%`};

  @media (max-width: 991px) {
    flex-direction: column;
  }
`;
const DesktopView = styled.div`
  align-items: center;

  @media (max-width: 991px) {
    display: none;
  }
`;

// The two header actions.
//
// They are real buttons, and they are the app's own: HeaderActionButton is
// already what BallScreen and ArsenalScreen put in their header rows, so this
// corner now matches them instead of carrying a third look invented for it.
// Bare text links were the mistake -- with no affordance and no boundary, two
// of them stacked read as one wrapped sentence rather than two things to
// press, whatever the spacing between them.
//
// Primary and secondary rather than two of the same: adding a bowler is the
// action this screen exists for, and scanning a pile of sheets is the
// occasional one.
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  /* Wraps at the narrow breakpoints rather than squeezing the labels. */
  flex-wrap: wrap;
  gap: 8px;
`;

const leftStyles = {
  display: "flex",
  justifyContent: "center",
  textAlign: "center",
  left: "12.5%",
  fontSize: 12,
  fontWeight: 900,
  zIndex: 11,
  color: "#000000",
};
const search_container = {
  width: "270px",
  height: "40px",
  fontWeight: "700",
  letterSpacing: "2px",
  fontSize: "12px",
  margin: "1rem 0",
  padding: "0 18px",
  border: "1px solid #e2e4ee",
  borderRadius: "100px",
  outline: "none",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 5% center",
  backgroundSize: "16px",
};
const tittleStyle = {
  textAlign: "left",
  font: "normal normal bold 28px/36px Lato",
  letterSpacing: "0px",
  color: "#0f1024",
  opacity: "1",
  fontWeight: "700",
  fontSize: "xx-large",
};

const ContainerException = styled.div`
  order: 4;
  width: 100%;
  height: 100%;
  height: -webkit-fill-available;
`;

const Marginrules = styled.div`
  margin: 0;
  width: 100%;
  height: 100%;
  height: -webkit-fill-available;
  max-width: 1024px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  -webkit-overflow-scrolling: touch;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* The scan button carries how much work is waiting, so nobody has to open
   the queue to find out there is nothing in it -- or that there are twelve. */
const ScanAction = styled.div`
  position: relative;
  display: inline-flex;
`;

const ScanBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -9px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #f5a524;
  color: #4a2f00;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #ffffff;
`;

const ListFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 0 24px;
  font: 600 13px/1 Poppins;
  letter-spacing: 0.3px;
  color: #8a90a6;
`;

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid #d4d7e3;
  border-top-color: #00187e;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const SearchSection = ({
  containerWidth,
  containerHeight,
  bowlers,
  userId,
  getCustomers,
  setCustomersFilter,
  setCustomer,
  deleteCustomer,
  filter,
  orderBy,
  isLoadingMore,
  hasMore,
  isPortrait,
  addBowlerLabel,
  isReadOnly,
  exportCustomers,
  current_woocommerce_id,
  woocommerceCheckUserExists,
  pluginSubscriptions,
  onScanQueue,
  scanPending = 0,
}) => {
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState(filter);
  const [orderBySelection, setOrderBySelection] = useState(
    orderBy.split(",")[0].replace("desc", "").replace(" ", "")
  );
  const [showPlaceholder, setShowPlaceholder] = useState(
    filter?.length > 0 ? false : true
  );

  const onSearchChange = () => {
    setCustomersFilter({ filter: filterValue });
    getCustomers({ loadMore: false, offset: 0 });
  };
  const gridWidth = containerWidth * 0.99;

  //  redefine max margin to 1024px
  const validateWidth = (width) => {
    if (width > 1024) {
      return 1024;
    } else {
      return width;
    }
  };
  //  redefine max margin to 1024px  0 8.173828125%
  const validateMargin = (width, margin) => {
    if (width > 1024) {
      return 7.673828125;
    } else {
      return margin + 3;
    }
  };

  // base value to define margin and columns
  const calculatedContainerWidth = validateWidth(containerWidth);
  const isIphone = calculatedContainerWidth < 768 && isPortrait;
  const gridHeight =
    containerHeight -
    (isIphone ? 0.28 * containerHeight : 0.175 * containerHeight);
  let columnsValue = isIphone
    ? 1
    : calculatedContainerWidth / (calculatedContainerWidth * 0.33);

  let columns = Math.trunc(columnsValue);
  const absoluteColumns = Math.abs(columnsValue);

  let marginAutoFill = Math.abs(columns - absoluteColumns) * 10;
  marginAutoFill = validateMargin(containerWidth, marginAutoFill);

  if (columns < 1) {
    columns = 1;
  }

  const getPhoto = useMemo(() => getPhotoForUserId(userId), [userId]);

  const filteredBowler = bowlers;
  const rows = Math.ceil(filteredBowler.length / columns);

  const rowHeight = isIphone
    ? (gridWidth * 156) / 271
    : (gridWidth * 0.33 * 156) / 271;

  const parentRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: rows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 4,
  });

  // Re-measure when the row height or column count change (resize/orientation).
  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowHeight, columns, rowVirtualizer]);

  const virtualItems = rowVirtualizer.getVirtualItems();

  // Infinite load: when we render close to the last loaded row, fetch the next page.
  useEffect(() => {
    if (!virtualItems.length || !hasMore || isLoadingMore || !getCustomers) {
      return;
    }
    const lastItem = virtualItems[virtualItems.length - 1];
    if (lastItem.index >= rows - PREFETCH_ROWS) {
      getCustomers({ loadMore: true });
    }
  }, [virtualItems, rows, hasMore, isLoadingMore, getCustomers]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const onChangeValue = (e) => {
    const value = e.target.value;
    setFilterValue(value);
    setShowPlaceholder(value.length > 0 ? false : true);
  };

  const resetSearch = () => {
    const value = "";
    setFilterValue(value);
    setShowPlaceholder(value.length > 0 ? false : true);
    setCustomersFilter({ filter: value });
    getCustomers({ loadMore: false, offset: 0 });
  };

  const handleAddBowler = () => {
    setCustomer({ bowler: {} });
  };

  const handleDelete = useCallback(
    (id) => {
      deleteCustomer && deleteCustomer({ id });
    },
    [deleteCustomer]
  );

  const handleOrderBy = (orderBy) => {
    if (orderBy === "lastname") {
      getCustomers({
        loadMore: false,
        offset: 0,
        orderBy: "lastname, firstname",
      });
    } else if (orderBy === "firstname") {
      getCustomers({
        loadMore: false,
        offset: 0,
        orderBy: "firstname, lastname",
      });
    } else if (orderBy === "telephone") {
      getCustomers({
        loadMore: false,
        offset: 0,
        orderBy: "telephone, lastname, firstname",
      });
    } else if (orderBy === "created_at") {
      getCustomers({
        loadMore: false,
        offset: 0,
        orderBy: "created_at desc, lastname, firstname",
      });
    } else if (orderBy === "updated_at") {
      getCustomers({
        loadMore: false,
        offset: 0,
        orderBy: "updated_at desc, lastname, firstname",
      });
    }

    setOrderBySelection(orderBy);
  };

  return (
    <Marginrules>
      <ContainerHeader marginFill={marginAutoFill}>
        <DesktopView className="col-12 col-md-5 col-lg-2">
          <span style={tittleStyle}> {t("form.label.bowlers")} </span>
        </DesktopView>
        <SearchContainer className="col-12 col-md-6 col-lg-8">
          <SearchGroup>
            <form onSubmit={handleSearch} style={{ margin: 0 }}>
              <DebounceInput
                id="searchBowler"
                className="form-control"
                value={filterValue}
                autoComplete="off"
                onChange={onChangeValue}
                name="searchText"
                style={search_container}
                {...{ ...DEBOUNCE_INPUT_PROPS, debounceTimeout: 300 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setTimeout(() => {
                      onSearchChange();
                    }, 300);
                  }
                }}
              />
              {showPlaceholder && (
                <Label
                  ShowPlaceholder={showPlaceholder}
                  htmlFor="searchBowler"
                  style={leftStyles}
                ></Label>
              )}
            </form>

            <ButtonSearch onClick={onSearchChange} aria-label={t("search")}>
              <Search />
            </ButtonSearch>
            <ButtonReset onClick={resetSearch} aria-label={t("reset")}>
              <XCircle />
            </ButtonReset>
          </SearchGroup>
          <div>
            <div style={selectStyle}>
              <FloatingLabelOrderBy
                label={t("order by")}
                className="arsenal-select-floating-label"
              >
                <SelectArsenal
                  className="arsenal-select"
                  value={orderBySelection}
                  options={[
                    { value: "lastname", label: t("customer-lastname") },
                    { value: "firstname", label: t("customer-name") },
                    { value: "telephone", label: t("customer-phone") },
                    { value: "created_at", label: t("customer-created_at") },
                    { value: "updated_at", label: t("customer-updated_at") },
                  ]}
                  onChange={handleOrderBy}
                />
              </FloatingLabelOrderBy>
            </div>
          </div>
        </SearchContainer>
        <DesktopView className="col-12 col-md-1 col-lg-2">
          <HeaderActions>
            {!isReadOnly && (
              <HeaderActionButton onClick={handleAddBowler}>
                {t("form.label.add_bowler")}
              </HeaderActionButton>
            )}
            {!isReadOnly && onScanQueue && (
              <ScanAction>
                <HeaderActionButton secondary onClick={onScanQueue}>
                  {t("specsheetScan.batch.button")}
                </HeaderActionButton>
                {/* Sheets read and still unreviewed -- work waiting, not
                    sheets uploaded. Nothing at all rather than a "0": an
                    empty queue is not a number worth reading. */}
                {scanPending > 0 && (
                  <ScanBadge title={t("specsheetScan.batch.pendingReview", { count: scanPending })}>
                    {scanPending}
                  </ScanBadge>
                )}
              </ScanAction>
            )}
          </HeaderActions>
        </DesktopView>
      </ContainerHeader>
      <ContainerException
        paddingRule={containerWidth}
        autoMargin={marginAutoFill}
      >
        {bowlers.length >= 1 ? (
          <ScrollArea ref={parentRef} style={{ height: gridHeight }}>
            <div
              style={{
                height: rowVirtualizer.getTotalSize(),
                width: "100%",
                position: "relative",
              }}
            >
              {virtualItems.map((virtualRow) => {
                const rowIndex = virtualRow.index;
                return (
                  <div
                    key={virtualRow.key}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: rowHeight,
                      transform: `translateY(${virtualRow.start}px)`,
                      display: "flex",
                    }}
                  >
                    {Array.from({ length: columns }).map((_, columnIndex) => {
                      const bowler =
                        filteredBowler[rowIndex * columns + columnIndex];
                      return (
                        <div
                          key={columnIndex}
                          style={{ width: `${100 / columns}%`, height: "100%" }}
                        >
                          {bowler && (
                            <BowlerCard
                              className={`bowler-column${columnIndex}-row${rowIndex}`}
                              id={bowler.id}
                              name={bowler.firstname}
                              lastName={bowler.lastname}
                              language={bowler.preferred_lang}
                              created_at={bowler.created_at}
                              updated_at={bowler.updated_at}
                              email={bowler.email}
                              phone={bowler.telephone}
                              specSheets={bowler.total_spec_sheets}
                              balls={bowler.total_balls}
                              orderBy={orderBySelection}
                              image={getPhoto(bowler.photo)}
                              onDelete={handleDelete}
                              isReadOnly={isReadOnly}
                              current_woocommerce_id={current_woocommerce_id}
                              woocommerceCheckUserExists={
                                woocommerceCheckUserExists
                              }
                              pluginSubscriptions={pluginSubscriptions}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {isLoadingMore ? (
              <ListFooter>
                <Spinner />
                {t("bowlers-loading-more")}
              </ListFooter>
            ) : !hasMore && bowlers.length > 0 ? (
              <ListFooter>{t("bowlers-end-of-list")}</ListFooter>
            ) : null}
          </ScrollArea>
        ) : (
          <div>
            {!isReadOnly && (
              <Button
                className="btn btn-primary"
                subs
                onClick={() => {
                  setCustomer({ bowler: {} });
                }}
              >
                {t("form.label.add_bowler")}
              </Button>
            )}
          </div>
        )}
      </ContainerException>
    </Marginrules>
  );
};

export default React.memo(
  Dimensions({
    containerStyle: {
      width: "100%",
      maxWidth: 1024,
      height: "-webkit-fill-available",
      minHeight: "100%",
      display: "flex",
      justifyContent: "center",
      padding: 0,
    },
  })(SearchSection)
);
